import React, { useState, useEffect } from 'react';
import { T, font, mono } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Btn, SearchBar, SBadge, matchesSearch } from './UI';
import { money } from '../utils/money';

const urgencyColors = {
  overdue: { color: T.danger, bg: T.dangerBg, label: 'Overdue', labelZh: '逾期' },
  due_soon: { color: T.orange || '#E8A83E', bg: '#E8A83E18', label: 'Due Soon', labelZh: '即将到期' },
  upcoming: { color: T.warn, bg: T.warnBg, label: 'Upcoming', labelZh: '即将' },
  ok: { color: T.success, bg: T.successBg, label: 'On Track', labelZh: '正常' },
  single_order: { color: T.muted, bg: T.surfaceAlt, label: '1 Order Only', labelZh: '仅1次' },
  snoozed: { color: '#8B8BE0', bg: '#8B8BE018', label: 'Snoozed', labelZh: '已延后' },
  dismissed: { color: T.dim, bg: T.surfaceAlt, label: 'Discontinued', labelZh: '已停售' },
  no_data: { color: T.dim, bg: T.surfaceAlt, label: 'No Data', labelZh: '无数据' },
};

const SNOOZE_OPTIONS = [
  { days: 7,  labelEn: 'Remind in 1 week',  labelZh: '1周后提醒' },
  { days: 14, labelEn: 'Remind in 2 weeks', labelZh: '2周后提醒' },
  { days: 21, labelEn: 'Remind in 3 weeks', labelZh: '3周后提醒' },
  { days: 30, labelEn: 'Remind in 1 month', labelZh: '1个月后提醒' },
];

function SnoozeModal({ lang, onSelect, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onCancel}>
      <Card style={{ width: '100%', maxWidth: 320, padding: 0, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: T.text }}>
            {lang === 'zh' ? '延后提醒' : 'Snooze Reminder'}
          </div>
          <div style={{ fontSize: 12, color: T.dim, marginTop: 2 }}>
            {lang === 'zh' ? '选择提醒时间' : 'Choose when to be reminded'}
          </div>
        </div>
        <div style={{ padding: '8px 0' }}>
          {SNOOZE_OPTIONS.map(opt => (
            <div key={opt.days} onClick={() => onSelect(opt.days)} style={{
              padding: '12px 20px', cursor: 'pointer', fontSize: 14,
              color: T.text, fontWeight: 600,
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {lang === 'zh' ? opt.labelZh : opt.labelEn}
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 20px 16px' }}>
          <Btn variant="ghostSm" onClick={onCancel} style={{ width: '100%', justifyContent: 'center' }}>
            {lang === 'zh' ? '取消' : 'Cancel'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

export default function AdminReorder({ lang, reload, Lk, onUrgentCountChange }) {
  const [predictions, setPredictions] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('urgent');
  const [expanded, setExpanded] = useState({});
  const [historyCache, setHistoryCache] = useState({});
  const [page, setPage] = useState(1);
  const [snoozeTarget, setSnoozeTarget] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const PAGE_SIZE = 50;

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    api.getReorderPrediction().then(setPredictions).catch(() => setPredictions([]));
  }, []);

  const refreshPredictions = () => {
    api.getReorderPrediction().then(preds => {
      setPredictions(preds);
      if (onUrgentCountChange) onUrgentCountChange();
    }).catch(() => setPredictions([]));
  };

  const filtered = predictions.filter(p => {
    if (filter === 'urgent' && !['overdue', 'due_soon'].includes(p.urgency)) return false;
    if (filter === 'has_data' && ['no_data', 'dismissed'].includes(p.urgency)) return false;
    if (filter === 'snoozed' && p.urgency !== 'snoozed') return false;
    if (filter === 'dismissed' && p.urgency !== 'dismissed') return false;
    if (search) {
      if (!matchesSearch(search, p.name) && !matchesSearch(search, p.sku || '')) return false;
    }
    return true;
  });

  const totalPages = filter === 'all' ? Math.ceil(filtered.length / PAGE_SIZE) : 1;
  const displayed = filter === 'all' ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : filtered;

  const doReorderSelected = async () => {
    const ids = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
    if (ids.length === 0) return;
    setLoading(true);
    for (const id of ids) {
      await api.toggleStock(id);
    }
    setSelected({});
    await reload();
    refreshPredictions();
    setLoading(false);
  };

  const doReorderSingle = async (id) => {
    setLoading(true);
    await api.updateProduct(id, { reorder_dismissed: 0, reorder_snoozed_until: null });
    await api.toggleStock(id);
    await reload();
    refreshPredictions();
    setLoading(false);
  };

  const doSnooze = async (id, days) => {
    setSnoozeTarget(null);
    setLoading(true);
    const snoozeDate = new Date();
    snoozeDate.setDate(snoozeDate.getDate() + days);
    await api.updateProduct(id, { reorder_snoozed_until: snoozeDate.toISOString().split('T')[0] });
    refreshPredictions();
    setLoading(false);
  };

  const doDismiss = async (id) => {
    setLoading(true);
    await api.updateProduct(id, { reorder_dismissed: 1 });
    refreshPredictions();
    setLoading(false);
  };

  const doUndismiss = async (id) => {
    setLoading(true);
    await api.updateProduct(id, { reorder_dismissed: 0 });
    refreshPredictions();
    setLoading(false);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleExpand = async (pid) => {
    if (expanded[pid]) {
      setExpanded({ ...expanded, [pid]: false });
      return;
    }
    if (!historyCache[pid]) {
      const data = await api.getOrderHistory(pid);
      setHistoryCache(prev => ({ ...prev, [pid]: (data || []).slice(0, 3) }));
    }
    setExpanded({ ...expanded, [pid]: true });
  };

  const ActionButtons = ({ p }) => {
    if (p.urgency === 'dismissed') {
      return (
        <Btn variant="ghostSm" onClick={() => doUndismiss(p.id)} disabled={loading} style={{ fontSize: 10, padding: '4px 8px' }}>
          {lang === 'zh' ? '恢复' : 'Restore'}
        </Btn>
      );
    }
    if (!p.in_stock) {
      return <SBadge label={lang === 'zh' ? '报价中' : 'Quoting'} color={T.warn} bg={T.warnBg} />;
    }
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Btn variant="small" onClick={() => doReorderSingle(p.id)} disabled={loading} style={{ fontSize: 11, padding: '5px 10px' }}>
          {lang === 'zh' ? '补货' : 'Reorder'}
        </Btn>
        <Btn variant="ghostSm" onClick={() => setSnoozeTarget(p.id)} disabled={loading} style={{ fontSize: 11, padding: '5px 10px', color: '#8B8BE0' }}>
          {lang === 'zh' ? '延后' : 'Later'}
        </Btn>
        <Btn variant="ghostSm" onClick={() => doDismiss(p.id)} disabled={loading} style={{ fontSize: 11, padding: '5px 10px', color: T.danger }}>
          {lang === 'zh' ? '停售' : "Don't Order"}
        </Btn>
      </div>
    );
  };

  return (
    <div>
      {snoozeTarget && (
        <SnoozeModal
          lang={lang}
          onSelect={days => doSnooze(snoozeTarget, days)}
          onCancel={() => setSnoozeTarget(null)}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: T.text }}>
          {lang === 'zh' ? '补货预测' : 'Reorder Prediction'}
          <span style={{ fontSize: 14, color: T.dim, fontWeight: 600, marginLeft: 8 }}>({predictions.length})</span>
        </h2>
        {selectedCount > 0 && (
          <Btn onClick={doReorderSelected} disabled={loading}>
            📦 {lang === 'zh' ? `发送 ${selectedCount} 个到报价` : `Send ${selectedCount} to Quoting`}
          </Btn>
        )}
      </div>

      {/* Sticky search + filters */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: T.bg, paddingTop: 4, paddingBottom: 8 }}>
        <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder={lang === 'zh' ? '搜索产品...' : 'Search products...'} style={{ marginBottom: 8 }} />
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            { key: 'urgent', label: lang === 'zh' ? '紧急' : 'Urgent' },
            { key: 'snoozed', label: lang === 'zh' ? '已延后' : 'Snoozed' },
            { key: 'dismissed', label: lang === 'zh' ? '已停售' : 'Discontinued' },
            { key: 'all', label: lang === 'zh' ? '全部' : 'All' },
          ].map(f => (
            <div key={f.key} onClick={() => { setFilter(f.key); setPage(1); }} style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', userSelect: 'none',
              background: filter === f.key ? T.accent : T.surfaceAlt,
              color: filter === f.key ? '#fff' : T.muted,
              border: `1px solid ${filter === f.key ? T.accent : T.border}`,
            }}>
              {f.label}
            </div>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16, marginTop: 8 }}>
        {[
          { label: lang === 'zh' ? '逾期' : 'Overdue', count: predictions.filter(p => p.urgency === 'overdue').length, color: T.danger },
          { label: lang === 'zh' ? '即将到期' : 'Due Soon', count: predictions.filter(p => p.urgency === 'due_soon').length, color: T.orange || '#E8A83E' },
          { label: lang === 'zh' ? '即将' : 'Upcoming', count: predictions.filter(p => p.urgency === 'upcoming').length, color: T.warn },
          { label: lang === 'zh' ? '正常' : 'On Track', count: predictions.filter(p => p.urgency === 'ok').length, color: T.success },
        ].map((c, i) => (
          <Card key={i} style={{ padding: '12px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: c.color, fontFamily: mono }}>{c.count}</div>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: 'uppercase' }}>{c.label}</div>
          </Card>
        ))}
      </div>

      {/* Mobile card list */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {displayed.map(p => {
            const u = urgencyColors[p.urgency] || urgencyColors.no_data;
            const isExpanded = expanded[p.id];
            const history = historyCache[p.id] || [];
            return (
              <Card key={p.id} hoverBg={p.urgency !== 'overdue'} style={{
                padding: 14,
                background: p.urgency === 'overdue' ? T.dangerBg : undefined,
                border: p.urgency === 'overdue' ? `1px solid ${T.danger}40` : undefined,
              }}>
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                  {/* Checkbox */}
                  <div style={{ paddingTop: 2, flexShrink: 0 }}>
                    {p.in_stock && p.urgency !== 'dismissed' ? (
                      <div onClick={() => setSelected({ ...selected, [p.id]: !selected[p.id] })} style={{
                        width: 20, height: 20, borderRadius: 4, border: `2px solid ${selected[p.id] ? T.accent : T.border}`,
                        background: selected[p.id] ? T.accent : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                      }}>
                        {selected[p.id] && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}
                      </div>
                    ) : <div style={{ width: 20 }} />}
                  </div>

                  {/* Name + SKU */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: T.text, wordBreak: 'break-word' }}>{p.name}</div>
                    {p.sku && <div style={{ fontSize: 11, color: T.dim, fontFamily: mono, marginTop: 1 }}>{p.sku}</div>}
                  </div>

                  {/* Status badge */}
                  <div style={{ flexShrink: 0 }}>
                    <SBadge label={lang === 'zh' ? u.labelZh : u.label} color={u.color} bg={u.bg} />
                  </div>
                </div>

                {/* Info row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 10, paddingLeft: 30 }}>
                  {p.predicted_next && (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ color: T.dim, fontWeight: 600 }}>{lang === 'zh' ? '预测: ' : 'Next: '}</span>
                      <span style={{
                        fontFamily: mono, fontWeight: 700,
                        color: p.urgency === 'overdue' ? T.danger : p.urgency === 'due_soon' ? (T.orange || '#E8A83E') : T.muted
                      }}>{p.predicted_next}</span>
                    </div>
                  )}
                  {p.last_price && (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ color: T.dim, fontWeight: 600 }}>{lang === 'zh' ? '上次价格: ' : 'Last: '}</span>
                      <span style={{ fontFamily: mono, color: T.muted }}>{money(p.last_price)}</span>
                    </div>
                  )}
                  {p.avg_qty && (
                    <div style={{ fontSize: 12 }}>
                      <span style={{ color: T.dim, fontWeight: 600 }}>{lang === 'zh' ? '平均数量: ' : 'Avg Qty: '}</span>
                      <span style={{ fontFamily: mono, color: T.muted }}>{p.avg_qty}</span>
                    </div>
                  )}
                  {p.urgency === 'snoozed' && p.reorder_snoozed_until && (
                    <div style={{ fontSize: 11, color: '#8B8BE0' }}>
                      {lang === 'zh' ? '至 ' : 'Until '}{p.reorder_snoozed_until}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ paddingLeft: 30 }}>
                  <ActionButtons p={p} />
                </div>

                {/* Expand toggle */}
                {p.reorder_count > 0 && (
                  <div onClick={() => toggleExpand(p.id)} style={{
                    marginTop: 10, paddingTop: 10,
                    borderTop: `1px solid ${T.border}40`,
                    fontSize: 11, color: T.accent, fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                  }}>
                    <span>{isExpanded ? '▼' : '▶'}</span>
                    {lang === 'zh' ? `历史 (${p.reorder_count}次)` : `History (${p.reorder_count}x)`}
                  </div>
                )}

                {/* Expanded history */}
                {isExpanded && (
                  <div style={{ marginTop: 8 }}>
                    {history.length === 0 ? (
                      <div style={{ fontSize: 11, color: T.dim, fontStyle: 'italic' }}>{lang === 'zh' ? '暂无历史记录' : 'No history yet'}</div>
                    ) : history.map((h, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '6px 0', borderBottom: i < history.length - 1 ? `1px solid ${T.border}30` : 'none',
                        fontSize: 12,
                      }}>
                        <div style={{ fontFamily: mono, color: T.dim }}>{h.date}</div>
                        <div style={{ color: T.text, flex: 1, marginLeft: 10 }}>{h.supplier_name || '—'}</div>
                        <div style={{ fontFamily: mono, color: T.accent, marginLeft: 8 }}>{h.price ? `${money(h.price)}` : '—'}</div>
                        <div style={{ fontFamily: mono, color: T.muted, marginLeft: 8, minWidth: 24, textAlign: 'right' }}>{h.qty || '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        /* Desktop table */
        <table style={{ width: '100%', fontSize: 12, fontFamily: font, borderCollapse: 'collapse' }}>
          <thead><tr>
            {['', lang === 'zh' ? '产品' : 'Product', lang === 'zh' ? '状态' : 'Status', lang === 'zh' ? '上次补货' : 'Last Reorder',
              lang === 'zh' ? '平均间隔' : 'Avg Interval', lang === 'zh' ? '预测下次' : 'Predicted Next', lang === 'zh' ? '平均数量' : 'Avg Qty',
              lang === 'zh' ? '上次价格' : 'Last Price', lang === 'zh' ? '操作' : 'Action'].map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '8px 10px', background: T.surfaceAlt, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}` }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {displayed.map(p => {
              const u = urgencyColors[p.urgency] || urgencyColors.no_data;
              const isExpanded = expanded[p.id];
              const history = historyCache[p.id] || [];
              return (
                <React.Fragment key={p.id}>
                <tr style={{ background: p.urgency === 'overdue' ? T.dangerBg : hoveredId === p.id ? '#EFF6FF' : 'transparent', cursor: 'pointer' }} onClick={() => toggleExpand(p.id)} onMouseEnter={() => setHoveredId(p.id)} onMouseLeave={() => setHoveredId(null)}>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}`, width: 30 }} onClick={e => e.stopPropagation()}>
                    {p.in_stock ? (
                      <div onClick={() => setSelected({ ...selected, [p.id]: !selected[p.id] })} style={{
                        width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected[p.id] ? T.accent : T.border}`,
                        background: selected[p.id] ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      }}>
                        {selected[p.id] && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}
                      </div>
                    ) : (
                      <SBadge label={lang === 'zh' ? '报价中' : 'Quoting'} color={T.warn} bg={T.warnBg} />
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: T.dim }}>{isExpanded ? '▼' : '▶'}</span>
                      <div>
                        <div style={{ fontWeight: 600, color: T.text }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: T.dim, fontFamily: mono }}>{p.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}` }}>
                    <SBadge label={lang === 'zh' ? u.labelZh : u.label} color={u.color} bg={u.bg} />
                    {p.reorder_count > 0 && <span style={{ fontSize: 10, color: T.dim, marginLeft: 4 }}>({p.reorder_count}x)</span>}
                    {p.urgency === 'snoozed' && p.reorder_snoozed_until && <div style={{ fontSize: 9, color: T.dim, marginTop: 2 }}>{lang === 'zh' ? '至' : 'until'} {p.reorder_snoozed_until}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.last_reorder || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.avg_interval ? `${p.avg_interval}d` : '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: p.urgency === 'overdue' ? T.danger : p.urgency === 'due_soon' ? (T.orange || '#E8A83E') : T.muted, fontWeight: ['overdue', 'due_soon'].includes(p.urgency) ? 700 : 400 }}>{p.predicted_next || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}`, fontFamily: mono, fontSize: 11 }}>{p.avg_qty || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}`, fontFamily: mono, fontSize: 11, color: T.muted }}>{p.last_price ? `${money(p.last_price)}` : '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: isExpanded ? 'none' : `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
                    <ActionButtons p={p} />
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan="9" style={{ padding: '0 10px 12px 40px', borderBottom: `1px solid ${T.border}`, background: T.surfaceAlt + '60' }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 6, marginTop: 4 }}>
                        {lang === 'zh' ? '最近订单历史' : 'Recent Order History'}
                      </div>
                      {history.length === 0 ? (
                        <div style={{ fontSize: 11, color: T.dim, fontStyle: 'italic' }}>{lang === 'zh' ? '暂无历史记录' : 'No history yet'}</div>
                      ) : (
                        <table style={{ width: '100%', fontSize: 11, fontFamily: font, borderCollapse: 'collapse' }}>
                          <thead><tr>
                            {[lang === 'zh' ? '日期' : 'Date', lang === 'zh' ? '供应商' : 'Supplier', lang === 'zh' ? '单价' : 'Price', lang === 'zh' ? '数量' : 'Qty'].map((h, i) => (
                              <th key={i} style={{ textAlign: 'left', padding: '4px 8px', color: T.dim, fontSize: 9, fontWeight: 800, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}40` }}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {history.map((h, i) => (
                              <tr key={i}>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${T.border}20`, fontFamily: mono, color: T.muted }}>{h.date}</td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${T.border}20`, color: T.text }}>{h.supplier_name || '—'}</td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${T.border}20`, fontFamily: mono, color: T.accent }}>{h.price ? `${money(h.price)}` : '—'}</td>
                                <td style={{ padding: '4px 8px', borderBottom: `1px solid ${T.border}20`, fontFamily: mono, color: T.accent }}>{h.qty || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {filtered.length === 0 && (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20, textAlign: 'center' }}>
          {lang === 'zh' ? '暂无预测数据' : 'No prediction data yet. Reorder products to build history.'}
        </div>
      )}

      {filter === 'all' && totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '16px 0', fontSize: 12 }}>
          <Btn variant="ghostSm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ fontSize: 11, padding: '4px 10px' }}>
            ← {lang === 'zh' ? '上一页' : 'Prev'}
          </Btn>
          <span style={{ color: T.muted, fontWeight: 700 }}>
            {lang === 'zh' ? `第 ${page} / ${totalPages} 页` : `Page ${page} of ${totalPages}`}
            <span style={{ color: T.dim, fontWeight: 400, marginLeft: 6 }}>({filtered.length} {lang === 'zh' ? '总计' : 'total'})</span>
          </span>
          <Btn variant="ghostSm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ fontSize: 11, padding: '4px 10px' }}>
            {lang === 'zh' ? '下一页' : 'Next'} →
          </Btn>
        </div>
      )}
    </div>
  );
}
